import { useCallback, useEffect, useRef } from 'react';

/**
 * 연속 스크롤의 "이 구간 필요함(onNeedRange)" 호출을 묶어서 한 번만 서버에 부른다 (2026-09-10).
 *
 * 왜: 월간·주간은 가장자리에 닿을 때마다 6주(42일)짜리 요청을 즉시 보냈다. 스크롤바를 끝에
 * 붙잡고 있으면 렌더가 끝나는 족족 다음 확장이 걸려 초당 15건까지 나갔다(실측: 5초 74건).
 * 서버가 죽을 정도는 아니어도 사용자별 분당 한도(rate limit)를 캘린더 혼자 다 써서 다른
 * 화면의 요청까지 429 로 막힐 수 있다.
 *
 * 하는 일 세 가지:
 *   1. 트레일링 디바운스 — 마지막 호출 뒤 delayMs 동안 조용하면 그때 한 번 보낸다. 스크롤이
 *      멈출 때 1건. (맨 처음 호출만 즉시 — 첫 화면이 150ms 늦게 뜰 이유가 없다.)
 *   2. 구간 합치기 — 기다리는 동안 들어온 구간들을 하나의 [min, max] 로 합친다. 확장 요청은
 *      늘 이어지는 6주 단위라 합치면 그냥 더 긴 한 구간이 된다.
 *   3. 진행 중 요청 취소 — 보낼 때 아직 응답이 안 온 이전 요청이 있으면 abort 하고 **그
 *      구간도 합쳐서** 보낸다. 취소한 구간을 새 요청이 덮지 않으면 그 자리가 빈 채로 남는
 *      기존 결함(방향을 급히 바꿀 때 한 덩어리가 유실)도 이걸로 같이 막힌다 — 호출부는
 *      성공한 뒤에만 "받은 구간" 을 갱신하므로 취소된 구간은 다음 델타에 자동으로 포함된다.
 *
 * 합친 구간이 MAX_SPAN_DAYS 를 넘으면(오래 끌어당긴 뒤) 마지막 요청이 있는 쪽 끝에서
 * MAX_SPAN_DAYS 만큼만 남긴다 — 화면(DOM)은 어차피 24주(168일)만 유지하니 그 창 + 한 덩어리
 * (6주)면 보이는 곳은 전부 덮이고, 나머지는 다시 그리로 스크롤할 때 확장 요청이 또 온다.
 * 서버 상한(365일) 아래이기도 하다.
 */
const DEFAULT_DELAY_MS = 150;
const MAX_SPAN_DAYS = 210; // 24주(DOM 창) + 6주(확장 한 덩어리).
const DAY_MS = 24 * 60 * 60 * 1000;

type Range = { from: Date; to: Date };

function union(a: Range, b: Range): Range {
  return { from: a.from < b.from ? a.from : b.from, to: a.to > b.to ? a.to : b.to };
}

/** 합친 구간이 너무 길면 latest 가 있는 쪽 끝을 남긴다. */
function clampToward(range: Range, latest: Range): Range {
  const span = range.to.getTime() - range.from.getTime();
  if (span <= MAX_SPAN_DAYS * DAY_MS) return range;
  const movingFuture = latest.to.getTime() >= range.to.getTime();
  return movingFuture
    ? { from: new Date(range.to.getTime() - MAX_SPAN_DAYS * DAY_MS), to: range.to }
    : { from: range.from, to: new Date(range.from.getTime() + MAX_SPAN_DAYS * DAY_MS) };
}

export function useCoalescedRange(
  load: (from: Date, to: Date, signal: AbortSignal) => Promise<void>,
  delayMs: number = DEFAULT_DELAY_MS,
): (from: Date, to: Date) => void {
  const loadRef = useRef(load);
  loadRef.current = load; // 최신 클로저(필터 상태 등)를 항상 쓴다 — 콜백 자체는 안정적으로 유지.
  const pendingRef = useRef<Range | null>(null);
  const latestRef = useRef<Range | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflightRef = useRef<{ range: Range; ctrl: AbortController } | null>(null);
  const firstRef = useRef(true);

  const dispatch = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;
    let range = pending;
    const inflight = inflightRef.current;
    if (inflight) {
      inflight.ctrl.abort();
      inflightRef.current = null;
      range = union(range, inflight.range);
    }
    range = clampToward(range, latestRef.current ?? pending);
    const ctrl = new AbortController();
    const entry = { range, ctrl };
    inflightRef.current = entry;
    loadRef.current(range.from, range.to, ctrl.signal).finally(() => {
      if (inflightRef.current === entry) inflightRef.current = null;
    });
  }, []);

  const request = useCallback((from: Date, to: Date) => {
    const r = { from, to };
    latestRef.current = r;
    pendingRef.current = pendingRef.current ? union(pendingRef.current, r) : r;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (firstRef.current) {
      firstRef.current = false;
      timerRef.current = null;
      dispatch();
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      dispatch();
    }, delayMs);
  }, [dispatch, delayMs]);

  // 언마운트 시 진행 중 요청을 취소한다. 단 StrictMode(개발)는 마운트 직후 한 번 "언마운트 →
  // 재마운트" 를 흉내 내는데, MonthView/TimeGridStream 의 최초 onNeedRange 는 mountedRef 가드로
  // 딱 한 번만 오므로 여기서 취소만 하면 첫 화면이 영영 안 뜬다(2026-09-10 실측: 요청 0건).
  // 그래서 취소한 구간을 pending 에 되돌려 두고, 재마운트 effect 가 그걸 바로 다시 보낸다 —
  // 진짜 언마운트면 effect 가 다시 안 돌아 아무 것도 안 나간다.
  useEffect(() => {
    if (pendingRef.current) dispatch();
    return () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      const inflight = inflightRef.current;
      if (inflight) {
        inflight.ctrl.abort();
        inflightRef.current = null;
        pendingRef.current = pendingRef.current ? union(pendingRef.current, inflight.range) : inflight.range;
      }
    };
  }, [dispatch]);

  return request;
}
